import React, { useCallback, useState } from 'react'
import { Badge, Card, Col, Dropdown, Row } from 'react-bootstrap'
import { FaEllipsisV } from 'react-icons/fa'
import { Project, ProjectAnnotationType } from '../types/models/project'
import { useNavigate } from 'react-router-dom'
import { useGetContentImageQuery } from '../services/contentApi'
import { useFetchAnnotatedContentQuery } from '../services/contentApi'
import { projectsApi, useDeleteProjectMutation } from '../services/projectsApi'
import { toTitleCase } from '../utils'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  clearFilterTags,
  selectSelectedProjectId,
  setActiveDatasetSubView,
  setActiveInferenceSubView,
  setActiveModelSubView,
  setSelectedProjectIdAndClearPrediction,
} from '../store/slices/projectSlice'
import {
  setDatasetSources,
  setTrainingSource,
  setValidationSource,
} from '../store/slices/datasetSlice'
import ConfirmDialog from './modals/ConfirmDialog'
import ErrorDialog from './modals/ErrorDialog'
import styled from 'styled-components'
import { apiTags } from '../services/apiTags'
import { useFetchModelsQuery } from '../services/modelsApi'
import { Model } from '../types/models/model'
import { clearSelectedModelId } from '../store/slices/modelSlice'

const StyledCard = styled(Card)<{ $angle: number; $intensity: number }>`
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.85) 0%, rgba(20, 20, 20, 0.9) 100%);
  border: 1px solid rgba(34, 211, 238, 0.18) !important;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(34, 211, 238, 0.35) !important;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(34, 211, 238, 0.2),
      0 0 20px rgba(34, 211, 238, 0.15);
  }

  .card-header {
    background: transparent;
    border: none;
    padding: 0;
    position: relative;
    height: 200px;
    overflow: hidden;
  }

  .card-body {
    background: transparent;
    padding: 1.5rem;
    border-top: 1px solid rgba(34, 211, 238, 0.1);
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .card-title {
    color: #ffffff;
    font-size: 1.25rem;
    margin-bottom: 12px;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .card-text {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    letter-spacing: 0.01em;
  }
`

const ProjectImage = styled(Card.Img)`
  height: 100%;
  width: 100%;
  object-fit: cover;
  transition: all 0.4s ease;
  filter: brightness(0.9) contrast(1.05);

  ${StyledCard}:hover & {
    transform: scale(1.08);
    filter: brightness(1) contrast(1.1);
  }
`

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(26, 26, 26, 0) 0%,
    rgba(26, 26, 26, 0.7) 70%,
    rgba(26, 26, 26, 0.95) 100%
  );
`

const TypeBadge = styled(Badge)<{
  $type: ProjectAnnotationType
}>`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  border-radius: 10px;
  z-index: 2;
  backdrop-filter: blur(10px);
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.2);

  ${(props) =>
    props.$type === 'classification'
      ? `
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.65) 0%, rgba(37, 99, 235, 0.5) 100%) !important;
      box-shadow: 0 4px 20px rgba(59, 130, 246, 0.4),
                  0 0 0 1px rgba(59, 130, 246, 0.5),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2);
      color: #DBEAFE;
      border-color: rgba(59, 130, 246, 0.4) !important;
      `
      : props.$type === 'oriented_bounding_box'
      ? `
      background: linear-gradient(135deg, rgba(34, 211, 238, 0.65) 0%, rgba(20, 184, 166, 0.5) 100%) !important;
      box-shadow: 0 4px 20px rgba(34, 211, 238, 0.4),
                  0 0 0 1px rgba(34, 211, 238, 0.5),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2);
      color: #E0F2FE;
      border-color: rgba(34, 211, 238, 0.4) !important;
      `
      : props.$type === 'bounding_box'
      ? `
      background: linear-gradient(135deg, rgba(168, 85, 247, 0.65) 0%, rgba(147, 51, 234, 0.5) 100%) !important;
      box-shadow: 0 4px 20px rgba(168, 85, 247, 0.4),
                  0 0 0 1px rgba(168, 85, 247, 0.5),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2);
      color: #E9D5FF;
      border-color: rgba(168, 85, 247, 0.4) !important;
      `
      : `
      background: linear-gradient(135deg, rgba(20, 184, 166, 0.65) 0%, rgba(6, 182, 212, 0.5) 100%) !important;
      box-shadow: 0 4px 20px rgba(20, 184, 166, 0.4),
                  0 0 0 1px rgba(20, 184, 166, 0.5),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2);
      color: #E0F2FE;
      border-color: rgba(20, 184, 166, 0.4) !important;
      `}
`

const ProjectStats = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 2;
  display: flex;
  gap: 16px;
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(20, 184, 166, 0.12) 100%);
  backdrop-filter: blur(12px);
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(34, 211, 238, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.875rem;
  font-weight: 500;

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.9;
  }
`

const MoreButton = styled(Dropdown.Toggle)`
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
  padding: 0.6rem;
  border-radius: 8px;
  background: rgba(34, 211, 238, 0.1);
  border: 1px solid rgba(34, 211, 238, 0.2);
  z-index: 10;
  position: relative;
  backdrop-filter: blur(4px);

  &:hover,
  &:focus,
  &:active {
    color: #22d3ee !important;
    background: rgba(34, 211, 238, 0.2) !important;
    border-color: rgba(34, 211, 238, 0.35) !important;
  }

  &::after {
    display: none;
  }
`

const StyledDropdownMenu = styled(Dropdown.Menu)`
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.95) 0%, rgba(20, 20, 20, 0.98) 100%);
  border: 1px solid rgba(34, 211, 238, 0.2);
  border-radius: 10px;
  padding: 0.5rem;
  margin-top: 0.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  z-index: 1000;

  .dropdown-item {
    color: rgba(255, 255, 255, 0.9);
    border-radius: 6px;
    padding: 0.6rem 1rem;
    transition: all 0.2s ease;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      background: linear-gradient(90deg, rgba(34, 211, 238, 0.15) 0%, rgba(20, 184, 166, 0.1) 100%);
      color: #22d3ee;
    }

    &.text-danger {
      color: #ef4444 !important;

      &:hover {
        background: rgba(239, 68, 68, 0.15);
        color: #ef4444 !important;
      }
    }
  }
`

interface ProjectCardProps {
  project: Project
  onProjectSelect?: () => void
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onProjectSelect }) => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const navigate = useNavigate()
  const [showConfirm, setShowConfirm] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [deleteProject] = useDeleteProjectMutation()
  const [error, setError] = useState<string>('')
  const [showError, setShowError] = useState(false)
  const { data: modelsData } = useFetchModelsQuery({ project_id: project.id })

  const gradientProps = React.useMemo(
    () => ({
      angle: 135,
      intensity: 1,
    }),
    []
  )

  const { data: contentResp } = useFetchAnnotatedContentQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      limit: 1,
      page: 1,
      sort_key: 'updated_at',
      sort_val: -1,
      metadata_filter: undefined,
    },
    { skip: !project || !selectedProjectId }
  )

  const [imageCacheTimestamp, setImageCacheTimestamp] = React.useState<number>(Date.now())

  // Listen for annotation updates to refresh images
  React.useEffect(() => {
    const handleAnnotationUpdate = () => {
      setImageCacheTimestamp(Date.now())
    }

    window.addEventListener('annotation-updated', handleAnnotationUpdate)
    window.addEventListener('reset-image-loading', handleAnnotationUpdate)

    return () => {
      window.removeEventListener('annotation-updated', handleAnnotationUpdate)
      window.removeEventListener('reset-image-loading', handleAnnotationUpdate)
    }
  }, [])

  const { data: imageUrl } = useGetContentImageQuery(
    {
      ContentID: contentResp?.content[0]?.id || '',
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      _t: imageCacheTimestamp,
    },
    { skip: !contentResp?.content[0]?.id || !project || !selectedProjectId }
  )

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if we're showing the confirm dialog
    if (showConfirm) {
      e.stopPropagation()
      return
    }

    // Clear all project navigation related localStorage items
    localStorage.removeItem('activeTab')
    localStorage.removeItem('selectedModelTab')
    localStorage.removeItem('activeDatasetSubView')
    localStorage.removeItem('activeInferenceSubView')
    localStorage.removeItem('activeModelSubView')
    localStorage.removeItem('inferenceLoop_currentPhase')
    localStorage.removeItem('compareModelId')

    dispatch(setTrainingSource([]))
    dispatch(setValidationSource([]))
    dispatch(setDatasetSources([]))
    dispatch(clearFilterTags())
    dispatch(setSelectedProjectIdAndClearPrediction(project.id))
    dispatch(clearSelectedModelId(project.id))
    dispatch(setActiveInferenceSubView('Overview'))
    dispatch(setActiveDatasetSubView('Overview'))
    dispatch(setActiveModelSubView('Overview'))
    navigate(`/overview/${project.id}/overview`)
    // Close modal if onProjectSelect is provided
    onProjectSelect?.()
    setTimeout(() => {
      dispatch(projectsApi.util.invalidateTags([apiTags.Project]))
    }, 1000)
  }

  const handleDelete = useCallback(
    async (e?: React.MouseEvent | React.SyntheticEvent) => {
      e?.stopPropagation()
      try {
        await deleteProject(project.id).unwrap()
        // Close the confirm dialog to prevent any bubbling
        setTimeout(() => {
          dispatch(projectsApi.util.invalidateTags([apiTags.Project]))
        }, 1000)
        setShowConfirm(false)
      } catch (error: unknown) {
        setError(error instanceof Error ? error.message : 'Failed to delete project')
        setShowError(true)
      }
    },
    [deleteProject, dispatch, project.id]
  )

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setShowConfirm(true)
    setShowDropdown(false)
  }, [])

  const deployedCount =
    modelsData?.models?.filter(
      (model: Model) => model.deployment.status.toLowerCase() === 'in_service'
    ).length || 0

  return (
    <StyledCard
      onClick={(e) => handleCardClick(e)}
      type={project.annotation_type}
      $angle={gradientProps.angle}
      $intensity={gradientProps.intensity}
    >
      <Card.Header>
        <ProjectImage variant="top" src={imageUrl || '/fa18.jpeg'} />
        <ImageOverlay />
        <TypeBadge $type={project.annotation_type as ProjectAnnotationType}>
          {toTitleCase(project.annotation_type)}
        </TypeBadge>
        <ProjectStats>
          <StatItem>
            <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
          </StatItem>
        </ProjectStats>
      </Card.Header>

      <Card.Body>
        <Row className="align-items-center">
          <Col>
            <Card.Title className="text-truncate mb-2">{project.name}</Card.Title>
            <Card.Text className="mb-0">
              Last updated: {new Date(project.updated_at).toLocaleDateString()}
            </Card.Text>
            <Card.Text>
              {deployedCount} Deployment
              {deployedCount === 0 || deployedCount > 1 ? 's' : ''}
            </Card.Text>
          </Col>
          <Col xs="auto">
            <Dropdown
              onClick={(e) => e.stopPropagation()}
              show={showDropdown}
              onToggle={(isOpen: boolean) => setShowDropdown(isOpen)}
            >
              <MoreButton>
                <FaEllipsisV />
              </MoreButton>
              <StyledDropdownMenu align="end">
                <Dropdown.Item className="text-danger" onClick={handleDeleteClick}>
                  Delete Project
                </Dropdown.Item>
              </StyledDropdownMenu>
            </Dropdown>
          </Col>
        </Row>
      </Card.Body>

      <ConfirmDialog
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirmRight={handleDelete}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmRightText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={error}
        title="Delete Project Error"
      />
    </StyledCard>
  )
}

export default ProjectCard
